// T-spin detection using the 3-corner rule with kick-aware "mini" classification.
//
// Rules (guideline + Tetr.io behavior):
//   - The last successful action must have been a rotation.
//   - Of the 4 cells diagonally adjacent to the T's center, at least 3 must be filled or walls.
//   - The two "front" corners (in the direction the T's flat side faces) determine full vs. mini:
//       both front corners filled => FULL t-spin
//       otherwise (only one front + both back filled) => MINI
//       Exception: if the last kick used was the 5th (last-resort, large offset), force full.

import type { ActivePiece, Rotation } from '../types';
import type { Board } from './board';

// For each rotation state, the offsets (relative to piece anchor at top-left of 3×3 bbox)
// of the 4 corners around the T's center cell (which is at offset (1,1)).
// We classify two of them as "front" (pointing in the direction the flat side faces).
//
// T spawn (rotation 0) has block pattern:
//   . X .
//   X X X
//   . . .
// The flat side is on top; the "front corners" are the two top corners (0,0) and (2,0).

const CORNERS: Record<Rotation, {
  front: readonly [readonly [number, number], readonly [number, number]];
  back: readonly [readonly [number, number], readonly [number, number]];
}> = {
  0: { front: [[0, 0], [2, 0]], back: [[0, 2], [2, 2]] },
  1: { front: [[2, 0], [2, 2]], back: [[0, 0], [0, 2]] },
  2: { front: [[0, 2], [2, 2]], back: [[0, 0], [2, 0]] },
  3: { front: [[0, 0], [0, 2]], back: [[2, 0], [2, 2]] },
};

export interface TSpinInfo {
  kind: 'none' | 'mini' | 'full';
}

export function detectTSpin(
  board: Board,
  piece: ActivePiece,
  lastMoveWasRotation: boolean,
  lastKickIndex: number, // 0..n-1, or -1 if no rotation
): TSpinInfo {
  if (piece.type !== 'T' || !lastMoveWasRotation) return { kind: 'none' };
  const c = CORNERS[piece.rotation];
  const isFilled = (dx: number, dy: number): boolean => {
    const x = piece.x + dx;
    const y = piece.y + dy;
    return board.isBlocked(x, y);
  };
  let count = 0;
  let frontFilled = 0;
  let backFilled = 0;
  for (const [dx, dy] of c.front) {
    if (isFilled(dx, dy)) { count++; frontFilled++; }
  }
  for (const [dx, dy] of c.back) {
    if (isFilled(dx, dy)) { count++; backFilled++; }
  }
  if (count < 3) return { kind: 'none' };
  // Full if both front corners filled, OR last kick was the "TST" 5th kick.
  if (frontFilled === 2) return { kind: 'full' };
  if (lastKickIndex >= 4) return { kind: 'full' }; // last-resort kick => promote to full
  return { kind: 'mini' };
}
